import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Proximiio, { ImageDetection } from "proximiio-js-library";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { t } from "i18next";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { FaLocationDot, FaFlag, FaCamera } from "react-icons/fa6";
import { IoCloseSharp } from "react-icons/io5";
import Feature from "proximiio-js-library/lib/models/feature";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn, getFilteredItems, getSortedPOIs } from "@/lib/utils";
import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";

import useMapStore from "@/store/mapStore";
import useFeaturesStore from "@/store/featuresStore";
import { excludedAmenities } from "@/store/data";

function RouteForm() {
  const features = useFeaturesStore((state) => state.features);
  const showCustomRoutePicker = useMapStore(
    (state) => state.showCustomRoutePicker,
  );
  const currentLang = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const startFeature = useMapStore((state) => state.startFeature);
  const finishFeature = useMapStore((state) => state.finishFeature);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const appSession = useMapStore((state) => state.appSession);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const filterCategories = useMapStore((state) => state.filterCategories);

  const [startSearch, setStartSearch] = useState("");
  const [finishSearch, setFinishSearch] = useState("");
  const [activeMenu, setActiveMenu] = useState<"start" | "finish" | undefined>(
    "start",
  );
  const [foundInTitle, setFoundInTitle] = useState<SortedPoiItemModel[]>([]);
  const [foundInDescription, setFoundInDescription] = useState<
    SortedPoiItemModel[]
  >([]);
  const [enableGVision, setEnableGVision] = useState(
    import.meta.env.VITE_WAYDINDING_ENABLE_GVISION === "true",
  );

  const setStartFeature = useMapStore((state) => state.setStartFeature);
  const setFinishFeature = useMapStore((state) => state.setFinishFeature);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const cancelRouting = useMapStore((state) => state.cancelRouting);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setRouteStart = useMapStore((state) => state.setRouteStart);

  useEffect(() => {
    if (startFeature?.id) {
      setActiveMenu("finish");
    }
  }, []);

  useEffect(() => {
    const search = activeMenu === "start" ? startSearch : finishSearch;
    const pois = getSortedPOIs({ features, currentLang, floors });

    const filtered = getFilteredItems({
      items: pois,
      search,
      language: currentLang,
    }) as SortedPoiItemModel[];
    const foundInTitle = filtered.filter((item) => !item.foundInDescription);
    const foundInDescription = filtered.filter(
      (item) => item.foundInDescription,
    );

    setFoundInTitle(foundInTitle);
    setFoundInDescription(foundInDescription);
  }, [startSearch, finishSearch, activeMenu, currentLang, features, floors]);

  useEffect(() => {
    if (!enableGVision) {
      return;
    }
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(function (devices) {
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setEnableGVision(videoDevices.length > 0);
      });
    } else {
      setEnableGVision(false);
    }
  }, [setEnableGVision, enableGVision]);

  const onSelectHandler = (poi: SortedPoiItemModel) => {
    const feature = features.find((item) => item.id === poi.id);
    if (feature) {
      if (activeMenu === "start") {
        setStartFeature(feature);
        if (!mobileMode) {
          setRouteStart(feature);
        }
      }
      if (activeMenu === "finish") {
        setFinishFeature(feature);
        setRouteFinish(feature);
        centerToFinish();
      }
      //save feature select log
      console.log("LOG: save feature select log");
      let featureAmenityCategory = "";
      const featureSection = feature?.properties?.amenity
        ? filterCategories.find((category) =>
            category.items.find((item) => {
              if (
                (Array.isArray(item.id) &&
                  item.id.includes(feature.properties.amenity)) ||
                item.id === feature.properties.amenity
              ) {
                featureAmenityCategory = item.title;
                return true;
              }
            }),
          )
        : undefined;

      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType:
          kioskMode && !mobileMode && activeKiosk?.name
            ? activeKiosk?.name
            : deviceType,
      };

      new Proximiio.InteractionLogger({
        interactionType: "select",
        targetElementType: "feature",
        targetElementId: feature ? feature.id : undefined,
        targetElementTitle: feature ? feature.properties.title : undefined,
        targetElementAmenity: t(featureAmenityCategory, { lng: "en" }),
        targetElementAmenityCategory: featureSection
          ? t(featureSection.title, { lng: "en" })
          : "",
        kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
        kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
        metadata: userData,
        language: currentLang,
        session: appSession,
      });
    }
    setActiveMenu(undefined);
    console.log("selected", feature);
  };

  const inputChangeHandler = (input: "start" | "finish", value: string) => {
    if (input === "start" && startFeature) {
      setStartFeature({} as Feature);
      setStartSearch(value);
    }
    if (input === "finish" && finishFeature) {
      setFinishFeature({} as Feature);
      setFinishSearch(value);
    }
  };

  const inputFocusHandler = (input: "start" | "finish") => {
    if (activeMenu === undefined) {
      setActiveMenu(input);
    }
    if (input === "start" && startFeature?.properties?.title) {
      setStartSearch(startFeature?.properties?.title);
    }
    if (input === "finish" && finishFeature?.properties?.title) {
      setFinishSearch(finishFeature?.properties?.title);
    }
  };

  const inputBlurHandler = () => {
    setTimeout(() => {
      if (!startFeature?.properties?.title) {
        // resetView();
      }
      // setActiveMenu(undefined);
    }, 500);
  };

  const onCloseHandle = () => {
    console.log("close handler");
    cancelRouting();
    /*if (input === "start") {
      setStartFeature({} as Feature);
      setStartSearch("");
    }
    if (input === "finish") {
      setFinishFeature({} as Feature);
      setFinishSearch("");
    }
    if (
      !activeMenu &&
      (!startFeature?.properties?.title || !finishFeature.properties?.title)
    ) {
      resetView();
    }*/
  };

  const recognizeLocationHandler = () => {
    ImageDetection.init(
      {
        gVisionApiKey: import.meta.env.VITE_WAYFINDING_GVISION_APIKEY,
        pois: getSortedPOIs({ features, currentLang, floors }).filter(
          (item) => {
            if (excludedAmenities.includes(item.properties.amenity))
              return false;
            return true;
          },
        ),
        cameraText: t("gvision-camera-text"),
        tryAgainButtonText: t("try-again"),
        confirmButtonText: t("confirm"),
        noResultsText: t("gvision-no-results"),
        resultsHeadingText: t("gvision-results-header"),
        returnResults: 1,
        dir: currentLang === "ar" ? "rtl" : "ltr",
      },
      (item) => {
        const feature = features.find((f) => f.id === item.id);
        if (feature) {
          setStartFeature(feature);
        }
      },
    );
  };

  return (
    <>
      {mobileMode && (
        <div className="absolute z-50 h-full w-full bg-background/80 backdrop-blur-sm"></div>
      )}
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "absolute left-3 right-3 top-3 z-100 flex max-h-96 flex-col rounded-xl p-5 lg:left-10 lg:top-10",
          activeMenu && "bg-white shadow-[0_3px_10px_rgb(0,0,0,0.05)]",
        )}
      >
        <div
          className="flex flex-col items-center gap-3"
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `var(--primary)`,
          }}
        >
          <div className="relative flex w-full items-center">
            <FaLocationDot className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
            <IoCloseSharp
              className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
              onClick={() => onCloseHandle()}
            />
            <Input
              type="text"
              placeholder={t("choose-start")}
              className="rounded-full border-2 border-primary px-10 text-base"
              disabled={activeMenu === "finish"}
              autoFocus={activeMenu === "start"}
              value={
                startFeature?.properties?.title
                  ? startFeature.properties.title
                  : startSearch
              }
              onChange={(e) => inputChangeHandler("start", e.target.value)}
              onFocus={() => inputFocusHandler("start")}
              onBlur={inputBlurHandler}
            />
          </div>
          {showCustomRoutePicker && (
            <div className="relative flex w-full items-center">
              <FaFlag className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
              <IoCloseSharp
                className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
                onClick={() => onCloseHandle()}
              />
              <Input
                type="text"
                placeholder={t("destination")}
                className="rounded-full border-2 border-primary px-10 text-base"
                disabled={activeMenu === "start"}
                autoFocus={activeMenu === "finish"}
                value={
                  finishFeature?.properties?.title
                    ? finishFeature.properties.title
                    : finishSearch
                }
                onChange={(e) => inputChangeHandler("finish", e.target.value)}
                onFocus={() => inputFocusHandler("finish")}
                onBlur={inputBlurHandler}
              />
            </div>
          )}
          {activeMenu !== undefined && (
            <div className="w-full">
              <OverlayScrollbarsComponent
                className={cn(
                  "max-h-60 overflow-auto group-data-[dir=ltr]:pr-7 group-data-[dir=rtl]:pl-7",
                  enableGVision && "max-h-48",
                )}
                options={{
                  scrollbars: { autoHide: "move", autoHideDelay: 3000 },
                  overflow: { x: "hidden" },
                }}
                defer
              >
                <div>
                  {foundInTitle.length > 0 && (
                    <ul className="flex flex-col gap-2">
                      <li className="font-semibold text-muted-foreground/80">
                        {t("suggestions")}
                      </li>
                      {foundInTitle.map((poi) => (
                        <li
                          key={poi.id}
                          value={poi.id}
                          className="flex cursor-pointer hover:text-primary"
                          onClick={() => onSelectHandler(poi)}
                        >
                          <span className="flex-1">
                            {poi.properties?.title}
                          </span>
                          <span className="font-bold">{poi.floorName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {foundInDescription.length > 0 && (
                    <ul className="flex flex-col gap-2">
                      <li className="mt-2 font-semibold text-muted-foreground/80">
                        {t("found-in-description")}
                      </li>
                      {foundInDescription.map((poi) => (
                        <li
                          key={poi.id}
                          value={poi.id}
                          className="flex cursor-pointer hover:text-primary"
                          onClick={() => onSelectHandler(poi)}
                        >
                          <span className="flex-1">
                            {poi.properties?.title}
                          </span>
                          <span className="font-bold">{poi.floorName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {foundInDescription.length === 0 &&
                    foundInTitle.length === 0 && (
                      <p className="py-4 text-center text-sm">
                        {t("no-results")}
                      </p>
                    )}
                </div>
              </OverlayScrollbarsComponent>
              {mobileMode && enableGVision && (
                <Button
                  onClick={() => recognizeLocationHandler()}
                  className="mx-auto flex h-auto gap-2 whitespace-normal rounded-full px-8 py-3 font-light"
                >
                  <FaCamera className="block text-lg lg:text-2xl" />
                  {t("locate-start")}
                </Button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </>
  );

  /*return (
    <>
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute left-3 right-3 top-3 z-10 flex flex-col rounded-xl bg-white p-3 shadow-[0_3px_10px_rgb(0,0,0,0.05)]"
      >
        <div className="flex flex-col items-center gap-1 lg:block lg:flex-row">
          {startFeature !== "kiosk" && (
            <PoiSelect
              selectedPoi={startFeature}
              onSelect={startSelectHandler}
              placeholder={t("choose-start")}
            />
          )}
          {showCustomRoutePicker && (
            <PoiSelect
              selectedPoi={finishFeature}
              onSelect={finishSelectHandler}
              placeholder={t("destination")}
            />
          )}
          {activeFilter?.type === "closest" && <ClosestAmenitySelect />}
        </div>
      </motion.div>
    </>
  );*/
}

export default RouteForm;
